import { useCallback, useEffect, useRef, useState } from 'react';

import { apiRequest } from '../api/client.js';

/**
 * Discord channel selection (ARCHITECTURE.md §65, §96).
 *
 * A dialog opened from the Discord connection card, not a panel of its own.
 * Panels on the Connections page are one-per-platform; a second kind of panel
 * sitting among them reads as another integration rather than as settings
 * belonging to one.
 *
 * Built on the native `<dialog>` element, which brings focus trapping, Escape
 * to dismiss, inertness of the page behind it and correct semantics for
 * assistive technology — all of which a hand-rolled overlay gets wrong by
 * default, and §65 requires keyboard navigation to work.
 */

type DiscordChannel = {
  channelId: string;
  name: string;
  type: number | null;
  enabled: boolean;
  /**
   * Whether Connaro's bot can actually see the channel.
   *
   * Distinct from `enabled`, which is the operator's choice. `null` means the
   * worker has not computed it yet.
   */
  botCanRead: boolean | null;
};

type DiscordGuild = {
  id: string;
  guildId: string;
  name: string;
  connectedAt: string;
  channels: DiscordChannel[];
};

export type DiscordChannelsDialogProps = {
  open: boolean;
  onClose: () => void;
};

export function DiscordChannelsDialog({
  open,
  onClose,
}: DiscordChannelsDialogProps): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [guilds, setGuilds] = useState<DiscordGuild[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      const data = await apiRequest<{ guilds: DiscordGuild[] }>('/discord/guilds');
      setGuilds(data.guilds);
      setError(undefined);
    } catch {
      setGuilds([]);
    }
  }, []);

  /*
   * showModal() rather than the `open` attribute. Only the former makes the
   * rest of the page inert and traps focus; setting `open` yields a dialog a
   * screen reader user can tab straight out of.
   */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    if (open && !dialog.open) {
      dialog.showModal();
      void load();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open, load]);

  const toggle = useCallback(
    async (channel: DiscordChannel) => {
      setSaving(channel.channelId);
      setError(undefined);

      try {
        await apiRequest(`/discord/channels/${channel.channelId}`, {
          method: 'PATCH',
          body: { enabled: !channel.enabled },
        });

        // Reload rather than assume: the server may refuse.
        await load();
      } catch (cause) {
        setError(
          cause instanceof Error ? cause.message : 'Could not change that channel. Try again.',
        );
      } finally {
        setSaving(undefined);
      }
    },
    [load],
  );

  /*
   * Counts what is ACTUALLY being read, not what is ticked.
   *
   * A channel selected before the bot lost access is still `enabled` in the
   * database, and counting it here would restate the original lie in the one
   * line of this dialog an operator is most likely to believe.
   */
  const enabledCount = (guilds ?? []).reduce(
    (total, guild) =>
      total +
      guild.channels.filter((channel) => channel.enabled && channel.botCanRead !== false).length,
    0,
  );

  const invisibleCount = (guilds ?? []).reduce(
    (total, guild) =>
      total + guild.channels.filter((channel) => channel.botCanRead === false).length,
    0,
  );

  return (
    <dialog
      ref={dialogRef}
      className="dialog"
      aria-labelledby="discord-channels-title"
      // Escape fires `cancel`; the backdrop and the close button both use
      // `close`. Reporting all of them keeps React's state in step with the
      // element's own, which otherwise drift apart the first time someone
      // presses Escape.
      onClose={onClose}
      onCancel={onClose}
    >
      <div className="dialog__header">
        <h2 className="dialog__title" id="discord-channels-title">
          Discord channels
        </h2>
        <button type="button" className="btn btn--ghost" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <div className="dialog__body">
        <p style={{ margin: '0 0 var(--s-4)', fontSize: 'var(--text-sm)' }}>
          Choose which channels Connaro reads. Nothing is read until you turn it on.
        </p>

        {error === undefined ? null : (
          <p role="alert" style={{ color: 'var(--c-danger)', fontSize: 'var(--text-sm)' }}>
            {error}
          </p>
        )}

        {guilds === undefined ? (
          <p style={{ fontSize: 'var(--text-sm)' }}>Loading…</p>
        ) : guilds.length === 0 ? (
          <p style={{ fontSize: 'var(--text-sm)' }}>
            No Discord server is connected to this workspace yet.
          </p>
        ) : (
          guilds.map((guild) => (
            <div key={guild.id} style={{ marginBottom: 'var(--s-5)' }}>
              <h3 style={{ fontSize: 'var(--text-sm)', margin: '0 0 var(--s-2)' }}>{guild.name}</h3>

              {guild.channels.length === 0 ? (
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--c-text-muted)' }}>
                  No channels found yet. Connaro lists them shortly after the bot joins.
                </p>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {guild.channels.map((channel) => {
                    /*
                     * A channel the bot cannot see can never deliver a message.
                     *
                     * Offering it as a normal choice is how an operator ends up
                     * selecting `#moderators-only`, posting in it, and asking
                     * why nothing appears — which is exactly what happened. The
                     * checkbox is refused, and the reason is written next to it
                     * rather than left to the disabled state to imply (§65).
                     */
                    const invisible = channel.botCanRead === false;

                    return (
                      <li key={channel.channelId} style={{ padding: 'var(--s-1) 0' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
                          <input
                            type="checkbox"
                            checked={channel.enabled && !invisible}
                            disabled={saving === channel.channelId || invisible}
                            onChange={() => {
                              void toggle(channel);
                            }}
                          />
                          <span>#{channel.name}</span>
                        </label>

                        {invisible && (
                          <p
                            style={{
                              margin: '2px 0 0 1.6rem',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--c-text-muted)',
                            }}
                          >
                            Connaro’s bot cannot see this channel. Give it access in Discord’s
                            channel permissions to read it here.
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))
        )}
      </div>

      <div className="dialog__footer">
        <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--c-text-muted)' }}>
          {/* Stated in words, never by the checkboxes alone (§65). */}
          {enabledCount === 0
            ? 'Connaro is not reading any Discord channel.'
            : `Connaro is reading ${String(enabledCount)} channel${enabledCount === 1 ? '' : 's'}.`}
          {invisibleCount > 0 &&
            ` ${String(invisibleCount)} channel${invisibleCount === 1 ? ' is' : 's are'} hidden from the bot.`}
        </p>
        <button type="button" className="btn" onClick={onClose}>
          Done
        </button>
      </div>
    </dialog>
  );
}
